/**
 * Eigenstaendige Huelle fuer den IT-Katalog.
 *
 * Der Katalog richtet sich an alle Mitarbeitenden, die Lizenzverwaltung an das
 * Controlling. Wer den Katalog aufruft, soll deshalb nicht in einem
 * Verwaltungswerkzeug landen: kein Dashboard, kein Laenderreport, keine
 * Administration in der Seitenleiste – nur der Katalog, die Sprachwahl und das
 * eigene Konto.
 *
 * Es ist dieselbe Anwendung mit derselben Anmeldung und denselben
 * Berechtigungen; getrennt ist ausschliesslich die Darstellung. Eine zweite
 * Anwendung haette eine zweite Anmeldung, eine zweite Registrierung und einen
 * zweiten Ort fuer Fehler bedeutet.
 */
import type { ReactNode } from 'react';
import { Link as RouterLink, NavLink } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Body1,
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Subtitle2,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  AppsRegular,
  BoxRegular,
  CartRegular,
  ClipboardTaskListLtrRegular,
  LaptopRegular,
  LightbulbRegular,
  CheckmarkCircleRegular,
  LocalLanguageRegular,
  SettingsRegular,
  SignOutRegular,
  PersonAddRegular,
} from '@fluentui/react-icons';
import { useCart } from '../lib/useCart.js';
import { NotificationBell } from './NotificationBell.js';
import { KRANNICH_BLACK, KRANNICH_YELLOW } from '../lib/brand.js';
import { AuditLogo } from './AuditLogo.js';
import { useSession } from '../lib/session.js';
import { useI18n, LOCALES, type LocaleCode } from '../i18n/index.js';
import { PERMISSIONS } from '../api/types.js';

const useStyles = makeStyles({
  shell: {
    // 100vh schliesst auf iOS die ein- und ausfahrenden Safari-Leisten mit ein;
    // die Seite ist dort also hoeher als das Sichtfeld. dvh misst, was
    // tatsaechlich sichtbar ist. vh bleibt als Rueckfall fuer aeltere Browser.
    minHeight: '100vh',
    '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalXL),
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
    flexWrap: 'nowrap',
    '@media (max-width: 640px)': {
      ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    },
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    textDecoration: 'none',
    color: tokens.colorNeutralForeground1,
  },
  actions: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  // Auf dem kleinsten Telefon (320 px) passen Glocke, Sprache und Konto nur
  // nebeneinander, wenn die Sprache allein als Symbol erscheint.
  languageLabel: { '@media (max-width: 400px)': { display: 'none' } },
  languageButton: { '@media (max-width: 400px)': { minWidth: 'auto' } },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    '@media (max-width: 640px)': { display: 'none' },
  },
  // Zwei Bereiche: der Katalog und die eigenen Geraete. Mehr wird es hier
  // bewusst nicht – alles Weitere gehört in die Lizenzverwaltung.
  nav: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    ...shorthands.padding(0, tokens.spacingHorizontalXL),
    '@media (max-width: 640px)': { ...shorthands.padding(0, tokens.spacingHorizontalM) },
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
    overflowX: 'auto',
  },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    color: tokens.colorNeutralForeground2,
    // Der Streifen liegt immer an, damit die Zeile beim Wechsel nicht springt.
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'transparent',
    ':hover': { color: tokens.colorNeutralForeground1, backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  navLinkActive: {
    color: tokens.colorBrandForeground1,
    borderBottomColor: tokens.colorBrandStroke1,
    fontWeight: tokens.fontWeightSemibold,
  },
  // minWidth 0: Ohne den Wert waere der Hauptbereich mindestens so breit wie
  // sein breitester Inhalt und die ganze Seite wuerde waagerecht scrollen.
  main: {
    flex: '1 1 auto',
    minWidth: 0,
    width: '100%',
    maxWidth: '82rem',
    marginInline: 'auto',
    ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalXL),
    '@media (max-width: 640px)': {
      ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalM),
    },
  },
  // Der Name darf schrumpfen, statt die Schaltflaechen aus der Zeile zu
  // draengen; minWidth 0 ist dafuer noetig, sonst ist ein Flex-Element
  // mindestens so breit wie sein Inhalt.
  brandName: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
});

export function CatalogShell({ children }: { children: ReactNode }) {
  const styles = useStyles();
  const { profile, has, signOut } = useSession();
  const { t, locale, setLocale } = useI18n();
  const { count: cartCount } = useCart();

  return (
    <div className={styles.shell}>
      <a className="skip-link" href="#hauptinhalt">
        Zum Hauptinhalt springen
      </a>

      <header className={styles.header}>
        <RouterLink className={styles.brand} to="/katalog">
          <AuditLogo size={36} title={t('catalog.title')} />
          {/* Nur der Name. Die Beschreibung stand hier und gleich darunter
              noch einmal im Seitenkopf – auf einem Telefon kostete die
              Dopplung das halbe Sichtfeld. */}
          <Subtitle2 className={styles.brandName}>{t('catalog.title')}</Subtitle2>
        </RouterLink>

        <div className={styles.actions}>
          <NotificationBell />
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button appearance="subtle" className={styles.languageButton} icon={<LocalLanguageRegular />} aria-label="Sprache wählen">
                <span className={styles.languageLabel}>{LOCALES[locale].label}</span>
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {(Object.keys(LOCALES) as LocaleCode[]).map((code) => (
                  <MenuItem key={code} onClick={() => setLocale(code)} disabled={code === locale}>
                    {LOCALES[code].label}
                  </MenuItem>
                ))}
              </MenuList>
            </MenuPopover>
          </Menu>

          <div className={styles.userInfo}>
            <Body1>{profile?.displayName ?? profile?.userPrincipalName ?? ''}</Body1>
          </div>

          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="transparent"
                aria-label={profile?.displayName ?? 'Benutzerkonto'}
                icon={<Avatar name={profile?.displayName ?? profile?.userPrincipalName ?? '?'} size={32} color="colorful" />}
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {/* Der Weg ins Lizenzmanagement erscheint nur, wer dort auch
                    etwas sehen darf – fuer alle anderen gaebe es nur eine
                    Meldung ueber fehlende Berechtigungen. */}
                {has(PERMISSIONS.DashboardRead) ? (
                  <MenuItem icon={<SettingsRegular />} onClick={() => window.location.assign('/dashboard')}>
                    {t('catalog.toLicenseManagement')}
                  </MenuItem>
                ) : null}
                <MenuItem icon={<SignOutRegular />} onClick={signOut}>
                  {t('auth.signOut')}
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </header>

      {/* Erscheint nur, wenn es tatsächlich etwas zu wechseln gibt. */}
      {has(PERMISSIONS.CatalogRead) &&
      (has(PERMISSIONS.AssetRead) ||
        has(PERMISSIONS.LicenseReadOwn) ||
        has(PERMISSIONS.CatalogRequest) ||
        has(PERMISSIONS.UserRequestCreate)) ? (
        <nav className={styles.nav} aria-label={t('catalog.title')}>
          <NavLink
            to="/katalog"
            className={({ isActive }) => mergeClasses(styles.navLink, isActive && styles.navLinkActive)}
          >
            <BoxRegular />
            {t('nav.catalog')}
          </NavLink>
          {has(PERMISSIONS.AssetRead) ? (
            <NavLink
              to="/meine-geraete"
              className={({ isActive }) => mergeClasses(styles.navLink, isActive && styles.navLinkActive)}
            >
              <LaptopRegular />
              {t('nav.myDevices')}
            </NavLink>
          ) : null}
          {has(PERMISSIONS.LicenseReadOwn) ? (
            <NavLink
              to="/meine-software"
              className={({ isActive }) => mergeClasses(styles.navLink, isActive && styles.navLinkActive)}
            >
              <AppsRegular />
              {t('nav.mySoftware')}
            </NavLink>
          ) : null}
          {/* Nur fuer die Gruppe, die Eintritte beantragt (Rolle UserRequester). */}
          {has(PERMISSIONS.UserRequestCreate) ? (
            <NavLink
              to="/katalog/userantrag"
              className={({ isActive }) => mergeClasses(styles.navLink, isActive && styles.navLinkActive)}
            >
              <PersonAddRegular />
              {t('userRequest.navNew')}
            </NavLink>
          ) : null}
          {has(PERMISSIONS.UserRequestCreate) && !has(PERMISSIONS.CatalogRequest) ? (
            <NavLink
              to="/katalog/meine-anfragen"
              className={({ isActive }) => mergeClasses(styles.navLink, isActive && styles.navLinkActive)}
            >
              <ClipboardTaskListLtrRegular />
              {t('nav.myRequests')}
            </NavLink>
          ) : null}
          {has(PERMISSIONS.CatalogRequest) ? (
            <>
              <NavLink
                to="/katalog/freigaben"
                className={({ isActive }) => mergeClasses(styles.navLink, isActive && styles.navLinkActive)}
              >
                <CheckmarkCircleRegular />
                {t('nav.approvals')}
              </NavLink>
              <NavLink
                to="/katalog/neue-software"
                className={({ isActive }) => mergeClasses(styles.navLink, isActive && styles.navLinkActive)}
              >
                <LightbulbRegular />
                {t('softwareRequest.navNew')}
              </NavLink>
              <NavLink
                to="/katalog/meine-anfragen"
                className={({ isActive }) => mergeClasses(styles.navLink, isActive && styles.navLinkActive)}
              >
                <ClipboardTaskListLtrRegular />
                {t('nav.myRequests')}
              </NavLink>
              {/* Die Zahl steht am Korb, nicht daneben: Sie ist der Grund,
                  ueberhaupt hinzusehen. */}
              <NavLink
                to="/katalog/warenkorb"
                className={({ isActive }) => mergeClasses(styles.navLink, isActive && styles.navLinkActive)}
                aria-label={t('cart.open')}
              >
                <CartRegular />
                {t('nav.cart')}
                {cartCount > 0 ? (
                  <Badge appearance="filled" size="small" style={{ backgroundColor: KRANNICH_YELLOW, color: KRANNICH_BLACK }}>
                    {cartCount}
                  </Badge>
                ) : null}
              </NavLink>
            </>
          ) : null}
        </nav>
      ) : null}

      <main id="hauptinhalt" className={styles.main}>
        {children}
      </main>
    </div>
  );
}
